/**
 * React hook for intake data — wraps the mode-aware data service.
 *
 * Components call this hook instead of importing mock arrays directly.
 * In mock mode, data resolves from local mocks; in live mode, from Medplum.
 */

import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MockProvider } from './mockProviders';
import type { IntakeLocation } from './intakeFhirMapping';
import type { IntakeAnswers } from './types';
import type { IntakeSubmissionResult } from './intakeDataService';
import {
  listIntakeProviders,
  listIntakeLocations,
  fetchProviderAvailabilityForDate,
  type ProviderAvailabilityForDate,
  submitIntake,
} from './intakeDataService';
import { listPracticeIntakeProviders } from './listPracticeIntakeProviders';
import { matchIntakeProviders, type MatchedProvider } from './intakeLeadPatient';
import type { SessionType } from './types';
import { ORG_TYPE_PROV, PRACTICE_INTAKE_LINK_TOKEN_EXT } from '@unify/fhir-model/constants';

function matchedProviderToMockProvider(m: MatchedProvider): MockProvider {
  const gender: 'male' | 'female' = m.gender === 'male' ? 'male' : 'female';
  const { referralTaskId: _referralTaskId, ...rest } = m;
  return { ...rest, gender };
}

const FETCH_TIMEOUT_MS = 45_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise
      .then((v) => {
        window.clearTimeout(t);
        resolve(v);
      })
      .catch((e) => {
        window.clearTimeout(t);
        reject(e);
      });
  });
}

export interface UseIntakeDataReturn {
  /** True when using mock/local data. */
  isMock: boolean;
  /** OAuth client_credentials session is ready for FHIR + Bot calls. */
  authReady: boolean;
  authError: string | null;
  /** All intake-eligible providers. */
  providers: MockProvider[];
  /** Practitioner id → referral-request Task id for reservation. */
  providerTaskMap: Record<string, string>;
  /** All intake-eligible locations. */
  locations: IntakeLocation[];
  /** True while providers are loading (mock resolves immediately). */
  providersLoading: boolean;
  /** True while locations are loading. */
  locationsLoading: boolean;
  providersError: string | null;
  locationsError: string | null;
  /** Loading state for initial data fetch (both lists). */
  loading: boolean;
  /** First error among provider/location fetches. */
  error: string | null;
  /** Re-fetch providers (pass answers + the round's required service HS ids for the matching bot). */
  refreshProviders: (answers?: IntakeAnswers, requiredHealthcareServiceIds?: string[]) => Promise<void>;
  /** Fetch bookable slots for a provider on a specific date (via bot). */
  fetchSlotsForDate: (
    providerId: string,
    sessionType: SessionType,
    dateKey: string,
  ) => Promise<ProviderAvailabilityForDate>;
  /** Submit completed intake. */
  submit: (
    answers: IntakeAnswers,
    opts?: { reservationPromoted?: boolean; episodeOfCareIds?: string[] },
  ) => Promise<IntakeSubmissionResult | null>;
  /** Resolved Organization id when intake was opened via a practice link token. */
  intakeOrganizationId: string | null;
}

export interface UseIntakeDataOptions {
  /** When false, skip FHIR reads until public intake OAuth is ready. */
  enabled?: boolean;
  authError?: string | null;
  /**
   * When true, load referral providers on mount (e.g. client intake directory).
   * Public intake defers to `refreshProviders()` before the provider step loading screen.
   */
  prefetchProviders?: boolean;
  /** Practice-specific intake link token. When present, scopes data to that organization. */
  intakeLinkToken?: string;
  /** Organization id from practice intake URL (?org=). Skips client Organization search when set. */
  intakeOrganizationId?: string;
  /** Staff add-client: scope provider list and SR performer to this practice Organization. */
  practiceOrganizationId?: string;
}

export function useIntakeData(opts?: UseIntakeDataOptions): UseIntakeDataReturn {
  const medplum = useMedplum();
  const live = true;
  const enabled = opts?.enabled !== false;
  const authError = opts?.authError ?? null;
  const prefetchProviders = opts?.prefetchProviders === true;
  const intakeLinkToken = opts?.intakeLinkToken;
  const intakeOrganizationIdFromLink = opts?.intakeOrganizationId?.trim() || null;
  const practiceOrganizationId = opts?.practiceOrganizationId?.trim() || null;

  const [providers, setProviders] = useState<MockProvider[]>([]);
  const [providerTaskMap, setProviderTaskMap] = useState<Record<string, string>>({});
  const [locations, setLocations] = useState<IntakeLocation[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  // Resolved organization id from intake link token
  const [tokenOrgId, setTokenOrgId] = useState<string | null | undefined>(undefined);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenResolving, setTokenResolving] = useState(false);

  // Resolve org id from intake link token (unless org id is already in the URL)
  useEffect(() => {
    if (intakeOrganizationIdFromLink) {
      setTokenOrgId(intakeOrganizationIdFromLink);
      setTokenError(null);
      setTokenResolving(false);
      return;
    }
    if (!intakeLinkToken || !enabled) {
      setTokenOrgId(null);
      setTokenError(null);
      return;
    }
    let cancelled = false;
    setTokenResolving(true);
    setTokenError(null);
    medplum
      .searchResources('Organization', { type: ORG_TYPE_PROV, _count: '200' })
      .then((orgs) => {
        if (cancelled) return;
        const match = orgs.find((o) =>
          o.extension?.some(
            (e) => e.url === PRACTICE_INTAKE_LINK_TOKEN_EXT && e.valueString === intakeLinkToken,
          ),
        );
        if (!match?.id) {
          setTokenError('This intake link is invalid or has expired. Please contact the practice for a new link.');
          setTokenOrgId(null);
        } else {
          setTokenOrgId(match.id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTokenError('Unable to validate the intake link. Please try again.');
          setTokenOrgId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTokenResolving(false);
      });
    return () => { cancelled = true; };
  }, [intakeLinkToken, intakeOrganizationIdFromLink, enabled, medplum]);

  const loadProviders = useCallback(
    async (answers?: IntakeAnswers, requiredHealthcareServiceIds?: string[]) => {
      setProvidersLoading(true);
      setProvidersError(null);
      try {
        if (practiceOrganizationId) {
          const practiceProviders = await withTimeout(
            listPracticeIntakeProviders(medplum, practiceOrganizationId),
            FETCH_TIMEOUT_MS,
            'Practice providers',
          );
          setProviders(practiceProviders);
          setProviderTaskMap({});
        } else if (answers) {
          const result = await withTimeout(
            matchIntakeProviders(medplum, {
              action: 'match-providers',
              topics: answers.topics,
              approaches: answers.specialties,
              providerPreferences: answers.demographicPrefs,
              language: answers.language,
              format: answers.format ?? '',
              availabilityTimeOfDay: answers.availabilityTimeOfDay,
              availabilityDays: answers.availabilityDays,
              dateOfBirth: answers.dateOfBirth,
              zip: answers.zip,
              requiredHealthcareServiceIds,
            }),
            FETCH_TIMEOUT_MS,
            'Providers',
          );
          const providerTaskMap: Record<string, string> = {};
          const providers = result.providers.map((m) => {
            providerTaskMap[m.id] = m.referralTaskId;
            return matchedProviderToMockProvider(m);
          });
          setProviders(providers);
          setProviderTaskMap(providerTaskMap);
        } else {
          const result = await withTimeout(
            listIntakeProviders({ live, medplum }),
            FETCH_TIMEOUT_MS,
            'Providers',
          );
          setProviders(result.providers);
          setProviderTaskMap(result.providerTaskMap);
        }
      } catch (err) {
        setProvidersError(err instanceof Error ? err.message : 'Failed to load providers');
        setProviders([]);
        setProviderTaskMap({});
      } finally {
        setProvidersLoading(false);
      }
    },
    [live, medplum, practiceOrganizationId],
  );

  const loadLocations = useCallback(
    async (organizationId?: string) => {
      setLocationsLoading(true);
      setLocationsError(null);
      try {
        const locs = await withTimeout(
          listIntakeLocations({ live, medplum, organizationId }),
          FETCH_TIMEOUT_MS,
          'Locations',
        );
        setLocations(locs);
      } catch (err) {
        setLocationsError(err instanceof Error ? err.message : 'Failed to load locations');
        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    },
    [live, medplum],
  );

  useEffect(() => {
    if (!enabled) return;
    // If a token is present, wait until it resolves before loading data
    if (intakeLinkToken && tokenOrgId === undefined) return;
    // Don't load if token resolved to invalid
    if (tokenError) return;
    if (prefetchProviders) {
      void loadProviders();
    }
    void loadLocations(tokenOrgId ?? undefined);
  }, [enabled, prefetchProviders, loadProviders, loadLocations, intakeLinkToken, tokenOrgId, tokenError]);

  const fetchSlotsForDate = useCallback(
    (providerId: string, sessionType: SessionType, dateKey: string) =>
      fetchProviderAvailabilityForDate({
        live,
        medplum,
        providerId,
        sessionType,
        date: dateKey,
      }),
    [live, medplum],
  );

  const submit = useCallback(
    (answers: IntakeAnswers, extra?: { reservationPromoted?: boolean; episodeOfCareIds?: string[] }) =>
      submitIntake({
        live,
        medplum,
        answers,
        providers,
        organizationId: practiceOrganizationId ?? tokenOrgId ?? undefined,
        reservationPromoted: extra?.reservationPromoted,
        episodeOfCareIds: extra?.episodeOfCareIds,
      }),
    [live, medplum, providers, practiceOrganizationId, tokenOrgId],
  );

  const loading = providersLoading || locationsLoading || tokenResolving;
  const error = authError ?? tokenError ?? providersError ?? locationsError;

  return useMemo(
    () => ({
      isMock: false,
      authReady: enabled,
      authError,
      providers,
      providerTaskMap,
      locations,
      providersLoading,
      locationsLoading,
      providersError,
      locationsError,
      loading,
      error,
      refreshProviders: loadProviders,
      fetchSlotsForDate,
      submit,
      intakeOrganizationId: practiceOrganizationId ?? tokenOrgId ?? null,
    }),
    [
      enabled,
      authError,
      providers,
      providerTaskMap,
      locations,
      providersLoading,
      locationsLoading,
      providersError,
      locationsError,
      loading,
      error,
      loadProviders,
      fetchSlotsForDate,
      submit,
      practiceOrganizationId,
      tokenOrgId,
    ],
  );
}
